import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Get or create session ID
function getSessionId() {
  const STORAGE_KEY = 'visitor_session_id';
  const SESSION_EXPIRY = 30 * 60 * 1000; // 30 minutes
  
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const { sessionId, timestamp } = JSON.parse(stored);
    // Check if session is still valid (not expired)
    if (Date.now() - timestamp < SESSION_EXPIRY) {
      return sessionId;
    }
  }
  
  // Generate new session ID
  const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    sessionId: newSessionId,
    timestamp: Date.now()
  }));
  
  return newSessionId;
}

// Debounce function to limit rapid calls
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Track visitor activity
async function trackActivity(sessionId, path, referrer) {
  try {
    await axios.post(`${API_BASE_URL}/visitor/activity`, {
      sessionId,
      path,
      referrer
    });
    // File is written immediately on the server when this succeeds
    console.debug('[Visitor Tracking] Activity recorded:', path);
  } catch (error) {
    // Silently fail - tracking should not break user experience
    console.debug('Visitor tracking error (non-critical):', error.message);
  }
}

// Debounced version of trackActivity
const debouncedTrack = debounce(trackActivity, 500);

/**
 * Hook to track visitor page visits
 * Automatically tracks route changes and sends to backend
 * Skips tracking for admin users
 */
export function useVisitorTracking() {
  const location = useLocation();
  const sessionIdRef = useRef(null);
  const previousPathRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(response.data.success === true);
        } catch (err) {
          // Token invalid or expired - silently handle (expected for non-admin users)
          setIsAdmin(false);
          // Clear invalid token
          if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');
          }
        }
      } else {
        setIsAdmin(false);
      }
    };
    
    checkAdmin();
    
    // Listen for storage changes (when login/logout happens)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === null) {
        checkAdmin();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    // Skip tracking if user is admin
    if (isAdmin) {
      return;
    }

    // Initialize session ID
    if (!sessionIdRef.current) {
      sessionIdRef.current = getSessionId();
    }

    // Get current path
    const currentPath = location.pathname + location.search;
    
    // Skip if same path (avoid duplicate tracking)
    if (currentPath === previousPathRef.current) {
      return;
    }
    
    previousPathRef.current = currentPath;
    
    // Get referrer (previous path or document.referrer)
    const referrer = document.referrer || previousPathRef.current || null;
    
    // Track activity (debounced)
    debouncedTrack(sessionIdRef.current, currentPath, referrer);
  }, [location.pathname, location.search, isAdmin]);
}

export default useVisitorTracking;

